import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Package, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGeo } from '../contexts/GeoContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice } = useGeo();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/orders' } } });
      return;
    }

    const fetchOrders = async () => {
      try {
        const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
        setOrders(data);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#525252] hover:text-[#0A0A0A]">Home</Link>
            <span className="text-[#525252]">/</span>
            <span className="font-semibold">My Orders</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-4xl font-black tracking-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
            MY ORDERS
          </h1>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 border-2 border-[#0A0A0A] skeleton"></div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16 border-2 border-[#0A0A0A] bg-white">
              <Package className="w-16 h-16 mx-auto mb-4 text-[#525252]" />
              <p className="text-xl font-semibold mb-2">No orders yet</p>
              <p className="text-[#525252] mb-6">Start shopping to see your orders here</p>
              <Link
                to="/products"
                className="inline-block bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] px-8 py-3 font-semibold hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
              >
                Shop Now
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="border-2 border-[#0A0A0A] bg-white"
                  data-testid={`order-${order.id}`}
                >
                  <div className="p-6 border-b-2 border-[#0A0A0A] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <p className="text-sm text-[#525252]">Order ID</p>
                      <p className="font-semibold">{order.id.slice(0, 8)}...</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#525252]">Date</p>
                      <p className="font-semibold">
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#525252]">Status</p>
                      <span className={`inline-block px-3 py-1 text-sm font-semibold ${
                        order.status === 'confirmed' 
                          ? 'bg-[#D5FDD8]' 
                          : order.status === 'pending' 
                          ? 'bg-[#FDF0D5]' 
                          : 'bg-[#FAD4D4]'
                      }`}>
                        {order.status.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-[#525252]">Total</p>
                      <p className="font-bold text-lg">{formatPrice(order.total)}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-sm font-semibold mb-4">
                      {order.items.length} item{order.items.length > 1 ? 's' : ''}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {order.items.slice(0, 4).map((item, idx) => (
                        <div
                          key={idx}
                          className="w-16 h-20 bg-[#F5F5F5] border-2 border-[#0A0A0A] flex items-center justify-center text-xs text-[#525252]"
                        >
                          {item.size || 'Item'}
                        </div>
                      ))}
                      {order.items.length > 4 && (
                        <div className="w-16 h-20 bg-[#F5F5F5] border-2 border-[#0A0A0A] flex items-center justify-center text-sm font-semibold">
                          +{order.items.length - 4}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
